"use client";

import { useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

import { brandsData } from "@/data/brands";

gsap.registerPlugin(useGSAP);

export const TrustedBrandsMarquee = () => {
  const marqueeRef = useRef<HTMLDivElement>(null);
  const tween = useRef<gsap.core.Tween | null>(null);

  useGSAP(
    () => {
      if (!marqueeRef.current) return;

      // Animating -50% shifts the first duplicate block completely out of view,
      // bringing the second duplicate block exactly into its starting position.
      tween.current = gsap.to(marqueeRef.current, {
        xPercent: -50,
        duration: 35,
        ease: "none",
        repeat: -1,
      });
    },
    { scope: marqueeRef }
  );

  return (
    <section className="w-full overflow-hidden border-y border-neutral-200 bg-white py-8">
      <div className="mx-auto max-w-[1500px] px-6">
        <p className="mb-8 text-xs font-bold uppercase tracking-[0.35em] text-neutral-500">
          Trusted by 1000+ Global Buyers
        </p>

        <div
          className="relative overflow-hidden"
          onMouseEnter={() => tween.current?.pause()}
          onMouseLeave={() => tween.current?.resume()} // Use resume() for smoother hover restoration
        >
          {/* Gradient Masks */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-white to-transparent" />

          {/* Marquee Track */}
          <div ref={marqueeRef} className="flex w-max items-center">
            {/* First Set */}
            <div className="flex shrink-0 items-center gap-16 pr-16 lg:gap-24 lg:pr-24">
              {brandsData.map((brand, index) => (
                <div
                  key={`brand-1-${brand.id || index}`}
                  className="group flex h-20 items-center justify-center shrink-0"
                >
                  <Image
                    src={brand.svgPath}
                    alt={brand.name}
                    width={180}
                    height={70}
                    className="h-10 w-auto grayscale transition-all duration-300 group-hover:scale-105 group-hover:grayscale-0"
                  />
                </div>
              ))}
            </div>

            {/* Second Set (Identical Clone for Seamless Infinite Loop) */}
            <div aria-hidden="true" className="flex shrink-0 items-center gap-16 pr-16 lg:gap-24 lg:pr-24">
              {brandsData.map((brand, index) => (
                <div
                  key={`brand-2-${brand.id || index}`}
                  className="group flex h-20 items-center justify-center shrink-0"
                >
                  <Image
                    src={brand.svgPath}
                    alt={brand.name}
                    width={180}
                    height={70}
                    className="h-10 w-auto grayscale transition-all duration-300 group-hover:scale-105 group-hover:grayscale-0"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};